import {
	baseFormattedDate,
	formatAmount,
	useLazyQuery,
	useMyUrl,
	useQuery,
	useTranslation
} from '@gimlite/common'
import {
	AutoComplete,
	Button,
	DatePicker,
	FilterExpanded,
	Form,
	Page,
	Table,
	Widget,
	Zone
} from '@gimlite/ui-v1'
import { useEffect, useMemo } from 'react'
import { Query, QuerySalesArgs } from '../../../../client/graphql'
import { sortAlphabetically } from '../../../common/array'
import { csvSalesGql } from '../gql/csvSales.gql'
import { SalesGql } from '../gql/sales.gql'
import {
	QuerySearchTariffsSalesArgs,
	searchTariffsSalesGql
} from '../gql/tariffs.gql'

export const SalesList = () => {
	const { getParamsUrl, setParamsUrl, clearParamsUrl } = useMyUrl()

	const { lang, t } = useTranslation()

	const salesQuery = useQuery<{ sales: Query['sales'] }, QuerySalesArgs>(
		SalesGql,
		{
			variables: {
				limit: 15,
				state: 'COMPLETED',
				...getParamsUrl
			}
		}
	)

	const tariffsQuery = useQuery<
		{ searchTariffs: Query['searchTariffs'] },
		QuerySearchTariffsSalesArgs
	>(searchTariffsSalesGql, {
		variables: {
			limit: 10,
			page: 1
		},
		notification: {
			error: "Une erreur s'est produite lors de la récupération des tarifs."
		}
	})

	const [csvSales, csvSalesState] = useLazyQuery<{
		csvSales: Query['csvSales']
	}>(csvSalesGql, {
		notification: {
			loading: 'Téléchargement des ventes en cours ...',
			success: 'Ventes téléchargés',
			error: 'Erreur lors du téléchargement des ventes'
		}
	})

	const sales = useMemo(() => {
		if (!salesQuery.data) return null

		return salesQuery.data.sales
	}, [salesQuery.data])

	const tarrifs = useMemo(() => {
		if (!tariffsQuery.data) return null

		return tariffsQuery.data.searchTariffs
	}, [tariffsQuery.data])

	useEffect(() => {
		if (csvSalesState.data?.csvSales.downloadURL) {
			window.open(csvSalesState.data?.csvSales.downloadURL, '_blank')
		}
	}, [csvSalesState.data])

	return (
		<Page>
			<Widget
				state={{
					error: salesQuery.error || tariffsQuery.error,
					loading: salesQuery.loading || tariffsQuery.loading,
					refetch: () => {
						salesQuery.refetch()
						tariffsQuery.refetch()
					}
				}}
				config={{ title: t('sales.title') }}
			>
				<Zone
					config={{
						zones: [['filter'], ['table']],
						rows: ['min-content', '1fr'],
						columns: ['1fr']
					}}
				>
					<Zone.Area config={{ area: 'filter' }}>
						<FilterExpanded
							data={{
								value: {
									tariffCode: getParamsUrl?.tariffCode,
									productBeginEnd: getParamsUrl?.productBeginEnd
								}
							}}
							handleEvent={{
								submit: (data: any) => {
									setParamsUrl({
										...getParamsUrl,
										...data,
										page: 1
									})
								}
							}}
						>
							<FilterExpanded.Fields>
								<Form.Item
									config={{
										name: 'tariffCode',
										label: t('sales.list.filter-tarifCode')
									}}
								>
									<AutoComplete
										data={{
											items: tarrifs
												? sortAlphabetically(
														tarrifs.list.map(({ code, _id }: any) => ({
															label: code,
															value: _id
														})),
														'label'
												  )
												: []
										}}
										config={{
											search: {
												caseSensitive: false,
												accentSensitive: false,
												pattern: 'start'
											}
										}}
									/>
								</Form.Item>
								<Form.Item
									config={{
										name: 'productBeginEnd',
										label: t('sales.list.filter-dateRange')
									}}
								>
									<DatePicker
										config={{
											mode: 'range',
											format: 'datetime'
										}}
										data={{ defaultValue: getParamsUrl?.productBeginEnd }}
									/>
								</Form.Item>
							</FilterExpanded.Fields>
							<FilterExpanded.Actions>
								<Button
									config={{
										text: t('sales.search'),
										type: { value: 'submit' }
									}}
								/>
								<Button
									handleEvent={{
										click: () => {
											clearParamsUrl()
										}
									}}
									config={{
										text: t('sales.clear-search'),
										disabled: !(
											getParamsUrl && Object.values(getParamsUrl).length > 0
										)
									}}
								/>
								<Button
									handleEvent={{
										click: () => {
											csvSales({
												variables: { ...(getParamsUrl ?? {}) }
											})
										}
									}}
									config={{
										text: 'CSV',
										icon: 'faFileDownloadSolid'
									}}
								/>
							</FilterExpanded.Actions>
						</FilterExpanded>
					</Zone.Area>
					<Zone.Area config={{ area: 'table' }}>
						<Table<{
							city: string
							_id: string
							segment: string
							amount: number
							currency: string
							payment: string
							channel: string
							tariffCode: string
							startDate: Date | null
							endDate: Date | null
						}>
							handleEvent={{
								paging: value => {
									setParamsUrl({
										...getParamsUrl,
										page: value.page
									})
								}
							}}
							data={
								sales
									? {
											list: sales.list.map(sale => ({
												city: sale.client?.name || '-',
												_id: sale._id || '',
												segment: sale.segment,
												amount: parseFloat((sale.amount / 100).toFixed(2)),
												currency: sale.currency,
												payment:
													sale?.payment?.mean?.type === 'CARD'
														? `${sale?.payment?.mean?.provider || ''} ${
																sale?.payment?.mean?.pan?.slice(-4) || ''
														  }`.trim() || '-'
														: sale?.payment?.mean?.type || '-',
												tariffCode: sale.products?.[0]?.tariffCode || '-',
												channel: sale?.source || '-',
												startDate: sale.products?.[0]?.startDate
													? new Date(sale.products[0].startDate)
													: null,
												endDate: sale.products?.[0]?.endDate
													? new Date(sale.products[0].endDate)
													: null
											})),
											paging: {
												current: sales.paging.current,
												count: sales.paging.count,
												limit: sales.paging.limit
											}
									  }
									: undefined
							}
							config={{
								columns: [
									{
										key: 'city',
										title: t('sales.list-city'),
										render: data => data.city,
										sort: {
											sorter: (a, b) =>
												`${a.city}`.toLowerCase() > `${b.city}`.toLowerCase()
													? -1
													: 1
										}
									},
									{
										key: 'startDate',
										title: t('sales.list-startDate'),
										render: data =>
											data.startDate
												? baseFormattedDate({
														date: data.startDate.toISOString(),
														lang
												  })
												: '-',
										sort: {
											sorter: (a, b) =>
												(b.startDate?.getTime() || 0) -
												(a.startDate?.getTime() || 0)
										}
									},
									{
										key: 'endDate',
										title: t('sales.list-endDate'),
										render: data =>
											data.endDate
												? baseFormattedDate({
														date: data.endDate.toISOString(),
														lang
												  })
												: '-',
										sort: {
											sorter: (a, b) =>
												(b.endDate?.getTime() || 0) -
												(a.endDate?.getTime() || 0)
										}
									},
									{
										key: 'amount',
										title: t('sales.list-amount'),
										render: data =>
											formatAmount({
												amount: data.amount / 100,
												currency: data.currency as
													| 'EUR'
													| 'USD'
													| 'CHF'
													| 'TOK'
													| null,
												lang
											}),
										sort: {
											sorter: (a, b) => (b.amount || 0) - (a.amount || 0)
										}
									},
									{
										key: 'payment',
										title: t('sales.list-payment'),
										render: data => data.payment || '-',
										sort: {
											sorter: (a, b) =>
												`${a.payment}`.toLowerCase() >
												`${b.payment}`.toLowerCase()
													? -1
													: 1
										}
									},
									{
										key: 'channel',
										title: t('sales.list-channel'),
										render: data => data?.channel,
										sort: {
											sorter: (a, b) =>
												`${a.channel}`.toLowerCase() >
												`${b.channel}`.toLowerCase()
													? -1
													: 1
										}
									},
									{
										key: 'tariffCode',
										title: t('sales.list-tarifCode'),
										render: data => data.tariffCode,
										sort: {
											sorter: (a, b) =>
												`${a.currency}`.toLowerCase() >
												`${b.currency}`.toLowerCase()
													? -1
													: 1
										}
									}
								]
							}}
						/>
					</Zone.Area>
				</Zone>
			</Widget>
		</Page>
	)
}
